import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, comics, type Comic } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

function SectionHeader({ kicker, title, desc, more }: { kicker: string; title: string; desc?: string; more?: string }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent-600">
          <span className="h-px w-6 bg-accent-500"></span> {kicker}
        </p>
        <h2 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-900">{title}</h2>
        {desc && <p className="mt-2 max-w-xl text-sm text-foreground-500">{desc}</p>}
      </div>
      {more && (
        <Link
          to={more}
          className="inline-flex items-center gap-1 rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
        >
          查看全部 <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

export function CategoryGrid() {
  return (
    <section className="bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          kicker="漫画分类"
          title="分类齐全，一网打尽"
          desc="从甜蜜爱情到热血动作，从奇幻世界到烧脑悬疑，五大热门分类任你挑选。"
        />
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {categories.map((c) => {
            const count = comics.filter((comic) => comic.categoryKey === c.key).length;
            return (
              <Link
                key={c.key}
                to={`/category?cat=${c.key}`}
                className="group relative overflow-hidden rounded-2xl border border-background-200 bg-background-100 p-6 transition-all duration-300 hover:border-primary-300 hover:bg-background-50"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${c.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-900">{c.name}</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">{c.desc}</p>
                <p className="mt-3 text-xs text-foreground-400">已收录 {count} 部作品</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function LatestSection() {
  const [active, setActive] = useState('all');
  const tabs = [{ key: 'all', name: '全部' }, ...categories];

  const list = useMemo(
    () => (active === 'all' ? comics : comics.filter((c) => c.categoryKey === active)),
    [active]
  );

  return (
    <section className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          kicker="最新更新"
          title="今日上架，抢先阅读"
          desc="第一时间带来最新话内容，追漫不再等待。"
          more="/category"
        />
        <div className="mt-6 inline-flex rounded-full border border-background-300 bg-background-50 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              className={`rounded-full px-3.5 py-1.5 text-sm transition-colors whitespace-nowrap ${
                active === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function RankingSection() {
  const ranking = useMemo(() => [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6), []);
  const badge = (i: number) =>
    i === 0 ? 'bg-secondary-500 text-background-50' : i === 1 ? 'bg-primary-500 text-background-50' : i === 2 ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-500';

  return (
    <section className="bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-secondary-600">
              <span className="h-px w-6 bg-secondary-500"></span> 人气榜单
            </p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-900">本周高分口碑榜</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              综合读者评分与追更热度实时生成，上榜作品皆是口碑与剧情双在线的高分韩漫。跟着榜单读，不容易踩雷。
            </p>
            <div className="mt-6 rounded-2xl border border-background-200 bg-background-100 p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                  <i className="ri-medal-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">评分体系</p>
                  <p className="text-xs text-foreground-500">读者真实评分 · 每日 24 点更新</p>
                </div>
              </div>
            </div>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 whitespace-nowrap"
            >
              浏览全部漫画 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="lg:col-span-3">
            <ol className="flex flex-col gap-3">
              {ranking.map((comic, i) => (
                <li key={comic.id}>
                  <Link
                    to={`/comic/${comic.id}`}
                    className="group flex items-center gap-4 rounded-2xl border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300 hover:bg-background-50"
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-base ${badge(i)}`}>
                      {i + 1}
                    </span>
                    <img
                      src={comic.cover}
                      alt={`${comic.title} 排名第${i + 1}`}
                      className="h-16 w-12 shrink-0 rounded-lg object-cover object-top"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="mt-1 truncate text-xs text-foreground-500">{comic.author} · {comic.category}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="inline-flex items-center gap-1 text-sm font-semibold text-secondary-700">
                        <i className="ri-star-fill text-secondary-500"></i> {comic.rating.toFixed(1)}
                      </p>
                      <p className="mt-1 text-xs text-foreground-400">{comic.views}人观看</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FeaturedSection() {
  const featured = [comics[0], comics[4], comics[7]];
  return (
    <section className="bg-accent-700 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent-100">
              <span className="h-px w-6 bg-accent-300"></span> 编辑精选
            </p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl text-background-50">本月编辑力荐</h2>
            <p className="mt-2 max-w-xl text-sm text-accent-100">由资深编辑精心挑选，剧情、画工与口碑俱佳，值得一读再读。</p>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {featured.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group relative overflow-hidden rounded-3xl"
            >
              <img
                src={comic.cover}
                alt={`${comic.title} 编辑推荐封面`}
                className="aspect-[4/5] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                  <i className="ri-star-fill text-secondary-400"></i> 编辑推荐
                </p>
                <h3 className="mt-3 font-heading text-xl text-background-50">{comic.title}</h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-background-200">{comic.description}</p>
                <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-secondary-300">
                  立即阅读 <i className="ri-arrow-right-line"></i>
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AboutStrip() {
  return (
    <section className="bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-6 rounded-3xl border border-background-200 bg-background-100 p-8 md:grid-cols-3 md:p-10">
          <div className="flex gap-4 md:col-span-1">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-background-50">
              <i className="ri-focus-3-line text-2xl"></i>
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground-900">只做精品</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">从海量新番中筛选题材与口碑俱佳之作，拒绝注水，专注优质阅读体验。</p>
            </div>
          </div>
          <div className="flex gap-4 md:col-span-1">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent-500 text-background-50">
              <i className="ri-refresh-line text-2xl"></i>
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground-900">同步更新</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">热门作品第一时间同步最新话，追更不掉队，情节不中断。</p>
            </div>
          </div>
          <div className="flex gap-4 md:col-span-1">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary-500 text-background-50">
              <i className="ri-device-line text-2xl"></i>
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground-900">多端适配</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-500">电脑、平板与手机随时切换，高清画面流畅呈现，通勤路上也能轻松追漫。</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqTeaser() {
  const faqs = [
    { q: '如何快速找到想看的漫画？', a: '你可以使用首页搜索框输入漫画名或作者，也可以进入分类页按爱情、奇幻、动作等类型筛选。' },
    { q: '网站每天什么时候更新漫画？', a: '热门连载作品一般会在每天 18:00 后陆续更新，节假日可能稍有延迟，刷新即可查看最新话。' },
    { q: '韩漫星球是免费阅读的吗？', a: '目前大部分作品提供免费试读与部分章节，具体以每部作品的展示为准。' },
  ];
  return (
    <section className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="text-center">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
            <span className="h-px w-6 bg-primary-500"></span> 常见问题
          </p>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-900">你可能想知道</h2>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          {faqs.map((f) => (
            <div key={f.q} className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-question-line text-primary-500"></i> {f.q}
              </h3>
              <p className="mt-2 pl-7 text-sm leading-7 text-foreground-500">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            查看更多问题 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

export function CtaBanner({ comic }: { comic: Comic }) {
  return (
    <section className="relative overflow-hidden">
      <img
        src={comic.cover}
        alt={`${comic.title} 章节阅读横幅`}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
      <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-secondary-300">正在热读</p>
          <h2 className="mt-3 font-heading text-2xl text-background-50 md:text-3xl">{comic.title}</h2>
          <p className="mt-3 text-sm leading-7 text-background-100">{comic.description.slice(0, 60)}…</p>
          <Link
            to={`/comic/${comic.id}`}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-play-circle-line"></i> 开始阅读
          </Link>
        </div>
      </div>
    </section>
  );
}